import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { Beer, Shuffle, RotateCcw, Trophy, X, Clock } from "lucide-react";
import { Wheel } from 'react-custom-roulette';

const BeerRoulette = () => {
    const [allBeers, setAllBeers] = useState([]);
    const [availableBeers, setAvailableBeers] = useState([]);
    const [selectedBeer, setSelectedBeer] = useState(null);
    const [mustSpin, setMustSpin] = useState(false);
    const [prizeNumber, setPrizeNumber] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [beerHistory, setBeerHistory] = useState([]);

    useEffect(() => {
        loadBeersAndHistory();
    }, []);

    const loadBeersAndHistory = async () => {
        try {
            // Fetch beers from Firestore
            const beersCollection = collection(db, "Restaurants", "AlmacenGDL", "beers");
            const beerSnapshot = await getDocs(beersCollection);
            const beerList = beerSnapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }))
                .filter(beer => beer.active === true) // Only active beers
                .sort((a, b) => {
                    const aId = isNaN(a.id) ? parseInt(a.id) || 0 : parseInt(a.id) || 0;
                    const bId = isNaN(b.id) ? parseInt(b.id) || 0 : parseInt(b.id) || 0;
                    return aId - bId;
                });

            setAllBeers(beerList);

            // Load history from localStorage
            const savedHistory = localStorage.getItem('beerRouletteHistory');
            const parsedHistory = savedHistory ? JSON.parse(savedHistory) : [];
            setBeerHistory(parsedHistory);

            // Filter out beers that are already in history
            const historyIds = parsedHistory.map(beer => beer.id);
            const availableList = beerList.filter(beer => !historyIds.includes(beer.id));
            setAvailableBeers(availableList);

        } catch (err) {
            setError("Error al cargar las cervezas: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatId = (id) => {
        if (id === null || id === undefined) return "00";
        const idStr = String(id);
        return idStr.length === 1 ? "0" + idStr : idStr;
    };

    const formatPrice = (price) => {
        const numPrice = Number(price);
        return isNaN(numPrice) ? "0.00" : numPrice.toFixed(2);
    };

    const getWheelData = () => {
        return availableBeers.map((beer, index) => ({
            option: formatId(beer.id),
            style: {
                backgroundColor: index % 2 === 0 ? '#F5E6D3' : '#E8D5B7',
                textColor: '#2C1810',
                fontSize: 18,
                fontWeight: 'bold'
            }
        }));
    };

    const handleSpinClick = () => {
        if (!mustSpin && availableBeers.length > 0) {
            const newPrizeNumber = Math.floor(Math.random() * availableBeers.length);
            setPrizeNumber(newPrizeNumber);
            setMustSpin(true);
        }
    };

    const handleStopSpinning = () => {
        setMustSpin(false);

        // Wait 0.5 seconds then show modal and update history
        setTimeout(() => {
            const selectedBeerData = availableBeers[prizeNumber];
            if (selectedBeerData) {
                setSelectedBeer(selectedBeerData);
                setShowModal(true);

                // Add to history with timestamp
                const newHistoryEntry = {
                    ...selectedBeerData,
                    timestamp: new Date().toISOString(),
                    time: new Date().toLocaleTimeString('es-ES', {
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                };

                const updatedHistory = [newHistoryEntry, ...beerHistory];
                setBeerHistory(updatedHistory);

                // Save to localStorage
                localStorage.setItem('beerRouletteHistory', JSON.stringify(updatedHistory));

                // Remove from available beers
                setAvailableBeers(prev => prev.filter(beer => beer.id !== selectedBeerData.id));
            }
        }, 500);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedBeer(null);
    };

    const resetRoulette = () => {
        if (mustSpin) return;

        // Clear history
        setBeerHistory([]);
        localStorage.removeItem('beerRouletteHistory');

        // Reset available beers to all active beers
        setAvailableBeers([...allBeers]);
        setSelectedBeer(null);
        setShowModal(false);
        setMustSpin(false);
        setPrizeNumber(0);
    };

    const spinAgain = () => {
        closeModal();
        setTimeout(() => {
            if (availableBeers.length > 0) {
                handleSpinClick();
            }
        }, 300);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-white">
                <div className="text-center p-4">
                    <div className="w-12 h-12 border-4 border-amber-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-amber-900 font-semibold">Cargando cervezas...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-white p-4">
                <div className="text-center p-4 bg-red-50 border-2 border-red-400 rounded-lg max-w-sm">
                    <p className="text-red-800 font-medium text-sm">{error}</p>
                    <button
                        onClick={loadBeersAndHistory}
                        className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    const wheelData = getWheelData();

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-100 p-3 md:p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-6">

                    <h1 className="text-3xl md:text-4xl font-black text-amber-900 mb-2 uppercase tracking-wide">
                        RULETA CERVECERA
                    </h1>
                    <p className="text-amber-700 font-medium text-sm md:text-base">
                        Deja que la suerte decida tu próxima cerveza
                    </p>
                </div>

                {/* Status Dashboard */}
                <div className="bg-amber-100 border-2 border-amber-800 rounded-xl p-4 mb-6">
                    <div className="flex justify-center items-center gap-6 text-center">
                        <div>
                            <div className="text-2xl md:text-3xl font-black text-amber-900">{availableBeers.length}</div>
                            <div className="text-xs font-bold text-amber-700 uppercase">Restantes</div>
                        </div>
                        <div className="w-px h-10 bg-amber-800"></div>
                        <div>
                            <div className="text-2xl md:text-3xl font-black text-amber-900">{beerHistory.length}</div>
                            <div className="text-xs font-bold text-amber-700 uppercase">Probadas</div>
                        </div>
                        <div className="w-px h-10 bg-amber-800"></div>
                        <div>
                            <div className="text-2xl md:text-3xl font-black text-amber-900">{allBeers.length}</div>
                            <div className="text-xs font-bold text-amber-700 uppercase">Total</div>
                        </div>
                    </div>

                    {availableBeers.length === 0 && (
                        <div className="mt-4 p-3 bg-green-100 border border-green-400 rounded-lg text-center">
                            <p className="text-green-800 font-bold text-sm">¡Felicidades! Has probado todas las cervezas</p>
                        </div>
                    )}
                </div>

                {/* Roulette Section */}
                <div className="bg-gradient-to-br from-amber-200 to-orange-200 border-3 border-amber-800 rounded-2xl p-4 md:p-6 mb-6">
                    <div className="text-center">
                        {/* Wheel */}
                        <div className="flex justify-center mb-6">
                            {availableBeers.length > 0 ? (
                                <div className="relative">
                                    <div className="bg-amber-100 border-4 border-amber-800 rounded-full p-2">
                                        <Wheel
                                            mustStartSpinning={mustSpin}
                                            prizeNumber={prizeNumber}
                                            data={wheelData}
                                            onStopSpinning={handleStopSpinning}
                                            backgroundColors={['#F5E6D3', '#E8D5B7']}
                                            textColors={['#2C1810']}
                                            outerBorderColor="#8B4513"
                                            outerBorderWidth={6}
                                            radiusLineColor="#8B4513"
                                            radiusLineWidth={2}
                                            fontSize={20}
                                            fontWeight="bold"
                                            textDistance={85}
                                            spinDuration={1.5}
                                        />
                                    </div>

                                    {/* Spinning indicator */}
                                    {mustSpin && (
                                        <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none">
                                            <div className="bg-amber-900 bg-opacity-90 text-amber-100 px-4 py-2 rounded-lg font-bold text-sm animate-pulse">
                                                <div className="flex items-center">
                                                    <RotateCcw className="h-4 w-4 mr-2 animate-spin" />
                                                    GIRANDO...
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="w-72 h-72 border-4 border-amber-800 rounded-full bg-amber-100 flex items-center justify-center">
                                    <div className="text-center">
                                        <Beer className="h-12 w-12 text-amber-600 mx-auto mb-3" />
                                        <p className="text-amber-800 font-semibold text-sm">No hay cervezas disponibles</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Controls */}
                        <div className="flex flex-col sm:flex-row justify-center gap-3">
                            <button
                                onClick={handleSpinClick}
                                disabled={mustSpin || availableBeers.length === 0}
                                className={`w-full sm:w-auto px-8 py-4 border-3 border-amber-800 rounded-xl font-black text-lg transition-all transform ${mustSpin || availableBeers.length === 0
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-amber-200 text-amber-900 hover:bg-amber-300 hover:scale-105 active:scale-95 shadow-lg"
                                    }`}
                            >
                                {mustSpin ? (
                                    <div className="flex items-center justify-center">
                                        <RotateCcw className="h-6 w-6 mr-3 animate-spin" />
                                        GIRANDO...
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center">
                                        <Shuffle className="h-6 w-6 mr-3" />
                                        GIRAR RULETA
                                    </div>
                                )}
                            </button>

                            {availableBeers.length === 0 && (
                                <button
                                    onClick={resetRoulette}
                                    className="w-full sm:w-auto px-8 py-4 border-3 border-amber-800 rounded-xl font-black text-lg bg-amber-800 text-amber-100 hover:bg-amber-900 transition-all transform hover:scale-105 active:scale-95 shadow-lg"
                                >
                                    <div className="flex items-center justify-center">
                                        <RotateCcw className="h-6 w-6 mr-3" />
                                        REINICIAR
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* History Section */}
                {beerHistory.length > 0 && (
                    <div className="bg-gradient-to-br from-amber-100 to-orange-100 border-3 border-amber-800 rounded-2xl p-4 md:p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl md:text-2xl font-black text-amber-900 uppercase flex items-center">
                                <Clock className="h-5 w-5 mr-2" />
                                Historial
                            </h3>
                            <span className="bg-amber-200 text-amber-900 px-2 py-1 rounded-lg text-sm font-bold">
                                {beerHistory.length} probadas
                            </span>
                        </div>

                        <div className="max-h-64 overflow-y-auto space-y-2">
                            {beerHistory.map((beer, index) => (
                                <div key={`${beer.id}-${beer.timestamp}`} className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="bg-amber-800 text-amber-100 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">
                                            {formatId(beer.id)}
                                        </div>
                                        <div>
                                            <p className="font-bold text-amber-900 text-sm">{beer.name}</p>
                                            <p className="text-amber-700 text-xs">{beer.brand}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-amber-700 text-xs">{beer.time}</p>
                                        <p className="text-amber-600 text-xs">#{index + 1}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Winner Modal - Compressed for mobile */}
                {showModal && selectedBeer && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
                        <div className="bg-gradient-to-br from-amber-100 to-orange-100 border-3 border-amber-800 rounded-xl max-w-sm w-full max-h-[85vh] overflow-y-auto">
                            {/* Compact Modal Header */}
                            <div className="bg-gradient-to-r from-amber-800 to-amber-900 text-amber-100 p-3 relative">
                                <button
                                    onClick={closeModal}
                                    className="absolute top-2 right-2 text-amber-100 hover:text-white transition-colors"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                                <div className="text-center">
                                    <div className="flex items-center justify-center mb-1">
                                        <Trophy className="h-5 w-5 mr-1" />
                                        <h2 className="text-lg font-black uppercase">¡Ganador!</h2>
                                    </div>
                                    <div className="text-4xl font-black">{formatId(selectedBeer.id)}</div>
                                </div>
                            </div>

                            {/* Compact Modal Content */}
                            <div className="p-4">
                                <div className="text-center">
                                    <h3 className="text-xl font-black uppercase text-amber-900 mb-1 leading-tight">
                                        {selectedBeer.name}
                                    </h3>

                                    {selectedBeer.type && (
                                        <p className="text-sm italic text-amber-700 mb-2">{selectedBeer.type}</p>
                                    )}

                                    <div className="bg-amber-200 border-2 border-amber-800 rounded-lg p-2 mb-3">
                                        <p className="text-lg font-bold text-amber-900">
                                            {selectedBeer.brand}
                                            {selectedBeer.alcohol && (
                                                <span className="font-normal text-sm ml-1">({selectedBeer.alcohol})</span>
                                            )}
                                        </p>
                                    </div>

                                    {/* Compact Modal Prices */}
                                    {selectedBeer.prices && Object.keys(selectedBeer.prices).length > 0 && (
                                        <div className="space-y-2 mb-4">
                                            {selectedBeer.prices['200ml'] && (
                                                <div className="bg-amber-100 border border-amber-800 rounded-lg p-2 text-center">
                                                    <div className="text-xl font-black text-amber-900">
                                                        ${formatPrice(selectedBeer.prices['200ml'])}
                                                    </div>
                                                    <div className="text-xs font-bold text-amber-700 uppercase">200 ML</div>
                                                </div>
                                            )}
                                            {selectedBeer.prices.lata && (
                                                <div className="bg-amber-100 border border-amber-800 rounded-lg p-2 text-center">
                                                    <div className="text-xl font-black text-amber-900">
                                                        ${formatPrice(selectedBeer.prices.lata)}
                                                    </div>
                                                    <div className="text-xs font-bold text-amber-700 uppercase">LATA</div>
                                                </div>
                                            )}
                                            {selectedBeer.prices['400ml'] && (
                                                <div className="bg-amber-100 border border-amber-800 rounded-lg p-2 text-center">
                                                    <div className="text-xl font-black text-amber-900">
                                                        ${formatPrice(selectedBeer.prices['400ml'])}
                                                    </div>
                                                    <div className="text-xs font-bold text-amber-700 uppercase">400 ML</div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Compact Modal Action Buttons */}
                                    <div className="space-y-2">
                                        <button
                                            onClick={closeModal}
                                            className="w-full px-4 py-2 bg-amber-800 text-amber-100 border-2 border-amber-800 rounded-lg font-bold hover:bg-amber-900 transition-all text-sm"
                                        >
                                            ¡Perfecto!
                                        </button>

                                        {availableBeers.length > 1 && (
                                            <button
                                                onClick={spinAgain}
                                                className="w-full px-4 py-2 bg-amber-200 text-amber-900 border-2 border-amber-800 rounded-lg font-bold hover:bg-amber-300 transition-all text-sm"
                                            >
                                                ¿Otra ronda?
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BeerRoulette;